import { useViewport } from '@sbt-web/hooks';
import type { Category } from '@sbt-web/category-utils';
import {
  Button,
  FormGroup,
  FormLabel,
  FullModal,
  SelectBoxSkeleton,
  SelectOption,
  SelectBox,
} from '@sbt-web/ui';
import classnames from 'classnames';
import React, { useCallback, useState } from 'react';
import InfoBadge from '../InfoBadge';
import { FilterURI, type ListFilter } from '@sbt-web/network/types';
import {
  mapValuesToOptions,
  useValueList,
  ValueListStatus,
  type ValueListOption,
} from '@client/hooks/useValueList';
import { useDataSource } from '@client/hooks/useDataSource';

import ownClasses from './list-selector.module.scss';
import commonClasses from '../SelectorStyles/common.module.scss';

type LegacySelectBoxType = 'default' | 'radio' | 'multi';

interface Props {
  filter: ListFilter;
  onSelect: (key: string, value?: string, label?: string) => void;
  category?: Category;
  initialValue?: string;
  dependenciesValues?: { [key: string]: string };
}

const minOptionsBeforeCollapse = 5;
const collapsedURIs: string[] = [
  FilterURI.AdvertiserType,
  FilterURI.MarketClothingGender,
];
const CLOTHING_TYPE_INFO =
  'Seleziona la Tipologia per attivare i filtri Marca e Taglia';

interface HelpTextData {
  title: string;
  content: { name: string; description?: string }[];
}

const buildHelpTextContent = ({ content }: HelpTextData) =>
  content.map(({ name, description }) => (
    <React.Fragment key={name}>
      <p
        className={classnames(
          'body-text',
          'semibold',
          ownClasses['value-name']
        )}
      >
        {name}
      </p>
      <p
        className={classnames(
          'body-text',
          'book',
          ownClasses['value-description']
        )}
      >
        {description}
      </p>
    </React.Fragment>
  ));

const buildInitialIndices = (
  initialValue: string | undefined,
  valueList: SelectOption[],
  filter: ListFilter
): number[] => {
  const initialIndices: number[] = [];
  if (initialValue) {
    if (filter.multiSelection) {
      const initialValues = initialValue.split(',');

      // Get the indices of the list based on the values already in the
      // query string to prepopulate the UI.
      for (let index = 0; index < valueList.length; index++) {
        const listItem = valueList[index];
        if (
          listItem.value &&
          initialValues.includes(listItem.value) &&
          valueList.find((v) => v.value === listItem.value)
        ) {
          initialIndices.push(index);
        }
      }
    } else {
      const index = valueList.findIndex((v) => v.value === initialValue);
      if (index !== -1) {
        initialIndices.push(index);
      }
    }
  }
  return initialIndices;
};

function buildHelpText(
  valueList: ValueListOption[],
  filter: ListFilter
): HelpTextData | undefined {
  if (valueList.some((value) => value.description)) {
    return {
      title: filter.label,
      content: valueList.map(({ name, description }) => ({
        name,
        description,
      })),
    };
  }
}

const ListSelector: React.FunctionComponent<Props> = ({
  filter,
  category,
  initialValue,
  onSelect,
  dependenciesValues,
}: Props) => {
  const { isMobile } = useViewport();

  const flavor = isMobile ? 'large' : 'small';

  const handleSelection = useCallback(
    (options: SelectOption[]) => {
      let newValue: string | undefined;
      let newLabel: string | undefined;
      if (options.length) {
        newValue = options.map((option) => option.value).join(',');
        newLabel = options.map((option) => option.name).join(', ');
      }
      onSelect(
        filter.queryString,
        newValue !== initialValue ? newValue : undefined,
        newValue !== initialValue ? newLabel : undefined
      );
    },
    [onSelect, filter, initialValue]
  );

  const getType = useCallback(
    (valueList: SelectOption[]): LegacySelectBoxType => {
      if (valueList.length === 1 || filter.multiSelection) {
        return 'multi';
      } else if (
        valueList.length < minOptionsBeforeCollapse &&
        !collapsedURIs.includes(filter.uri)
      ) {
        return 'radio';
      } else {
        return 'default';
      }
    },
    [filter]
  );

  const getExpanded = useCallback(
    (valueList: SelectOption[]): boolean => {
      if (collapsedURIs.includes(filter.uri)) {
        return false;
      } else {
        return valueList.length < minOptionsBeforeCollapse;
      }
    },
    [filter]
  );

  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  const openModal = useCallback(() => {
    setIsHelpModalOpen(true);
  }, [setIsHelpModalOpen]);

  const closeModal = useCallback(() => {
    setIsHelpModalOpen(false);
  }, [setIsHelpModalOpen]);

  const showClothingTypeInfo = filter.uri === FilterURI.MarketClothingType;

  const { dataSource, dependenciesFilled } = useDataSource(
    filter.dataSource,
    filter.dependencies,
    dependenciesValues
  );

  const valuelistData = useValueList(
    dataSource,
    category?.id,
    !dependenciesFilled
  );

  if (valuelistData.state === ValueListStatus.ERROR) return;

  const valueList = mapValuesToOptions(valuelistData.value);
  const initialIndices = buildInitialIndices(initialValue, valueList, filter);
  const helpText = buildHelpText(valueList, filter);
  const hasHelpText = !!helpText;

  return (
    <FormGroup>
      <div
        className={classnames(commonClasses['filler-div'], {
          [commonClasses['label-with-extra']]:
            hasHelpText || showClothingTypeInfo,
        })}
      >
        <FormLabel>{filter.label}</FormLabel>

        {hasHelpText ? (
          <InfoBadge
            onClick={openModal}
            label={filter.label}
            // Dont' remove this class, it's used in the e2e tests
            className={`${filter.queryString}-help-button`}
          />
        ) : null}

        {hasHelpText ? (
          <FullModal
            open={isHelpModalOpen}
            title={helpText.title}
            onClose={closeModal}
            actions={[
              <Button key="closer" design="solid" onClick={closeModal}>
                Ok, ho capito
              </Button>,
            ]}
          >
            {buildHelpTextContent(helpText)}
          </FullModal>
        ) : null}

        <ClothingTypeInfo filter={filter} />
      </div>

      {valuelistData.state === ValueListStatus.LOADED ? (
        <SelectBox
          name={filter.uri}
          type={getType(valueList)}
          expanded={getExpanded(valueList)}
          placeholder="Qualsiasi"
          selectedIndices={initialIndices}
          nullable
          flavor={flavor}
          valueList={valueList}
          onSelect={handleSelection}
        />
      ) : (
        <SelectBoxSkeleton expanded={filter.multiSelection} flavor={flavor} />
      )}

      {showClothingTypeInfo && (
        <p
          className={classnames(
            'body-text',
            'small',
            commonClasses['mobile-help-txt'],
            ownClasses['clothing-info-bottom-text']
          )}
        >
          {CLOTHING_TYPE_INFO}
        </p>
      )}
    </FormGroup>
  );
};

const ClothingTypeInfo = ({ filter }: { filter: ListFilter }) => {
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const closeModal = () => setInfoModalOpen(false);
  const openModal = () => setInfoModalOpen(true);

  if (filter.uri !== FilterURI.MarketClothingType) return;

  return (
    <>
      <InfoBadge
        label={filter.label}
        onClick={openModal}
        className={ownClasses['clothing-info-badge']}
      />

      <FullModal
        open={infoModalOpen}
        title={filter.label}
        onClose={closeModal}
        actions={[
          <Button key="closer" design="solid" onClick={closeModal}>
            Ok, ho capito
          </Button>,
        ]}
      >
        {CLOTHING_TYPE_INFO}
      </FullModal>
    </>
  );
};

export default ListSelector;
